import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Typography, Flex, Tag, Table, Input, DatePicker, Button, message, Popconfirm, Row, Col, Select } from "antd";
import { SearchOutlined, FileTextOutlined, PlusOutlined, EditOutlined, DeleteOutlined, StarFilled, StarOutlined, FolderOpenOutlined, UploadOutlined, EyeOutlined, DownloadOutlined, FilePdfOutlined, FileImageOutlined } from "@ant-design/icons";
import {
  Building02Icon,
  Time02Icon,
  GitForkIcon,
  Calendar01Icon,
  Folder01Icon,
  Invoice01Icon,
  Calculator01Icon,
  NoteEditIcon,
  ArrowLeft02Icon,
} from "hugeicons-react";
import dayjs from "dayjs";
import ScheduleFollowupView from "../schedule-followup/ScheduleFollowupView";
import CompanyTimeline from "./CompanyTimeline";
import { getInvoices } from "../../../services/invoiceService";
import { getQuotations } from "../../../services/quotationService";
import { getProposals } from "../../../services/proposalService";
import { getDeals } from "../../../services/pipelineService";
import { updateLeadOverview, updateLeadDirector, createLead, deleteLead, updateLeadFlags, getCompanyProfile, uploadCompanyDocument, deleteCompanyDocument, addLeadRemark, getCompanyHistory } from "../../../services/leadService";
import { formatDisplayDate, formatAmountWithCommas } from "../../../utils/formatters";
import { useSelector } from "react-redux";
import { getCookie } from "../../../utils/cookieUtils";
import "./CompanyFullViewModal.css";

const { Title, Text } = Typography;

const formatFileSize = (sizeInBytes) => {
  if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  }
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const renderDocIcon = (type = "", fileType = "") => {
  const t = String(type).toLowerCase();
  const ft = String(fileType).toLowerCase();
  if (ft.includes("pdf") || t.includes("gst") || t.includes("cert") || t.includes("pan") || t.includes("agree")) {
    return (
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "#fee2e2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
        <FilePdfOutlined />
      </div>
    );
  }
  if (ft.includes("image") || ft.includes("png") || ft.includes("jpg") || ft.includes("jpeg")) {
    return (
      <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
        <FileImageOutlined />
      </div>
    );
  }
  return (
    <div style={{ width: 40, height: 40, borderRadius: 10, background: "#e0f2fe", color: "#0284c7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
      <FileTextOutlined />
    </div>
  );
};

const combineProposalsAndQuotations = (quoteRes, propRes) => [
  ...(quoteRes?.quotations || []).map((qt) => ({
    ...qt,
    proposalNumber: qt.quotationNumber,
    proposalDate: qt.quotationDate,
    templateName: qt.kindNote || "Commercial Quotation",
    amount: `₹ ${Number(qt.total || 0).toLocaleString("en-IN")}`,
    status: "Quoted",
  })),
  ...(propRes?.proposals || []).map((pr) => ({
    ...pr,
    amount: pr.totalValue ? `₹ ${Number(pr.totalValue).toLocaleString("en-IN")}` : "Custom Scope",
  })),
];

const fetchAdminCompanyData = async (effectiveCompanyCode, q, setters) => {
  const { setInvoices, setProposals } = setters;
  try {
    const res = await getInvoices(effectiveCompanyCode, { search: q });
    if (res?.success) {
      setInvoices(res.invoices || []);
    }
  } catch (e) {
    console.error("[CompanyFullViewModal] Error fetching invoices:", e);
  }

  try {
    const [quoteRes, propRes] = await Promise.all([getQuotations(effectiveCompanyCode, { search: q }), getProposals(effectiveCompanyCode, { search: q })]);
    setProposals(combineProposalsAndQuotations(quoteRes, propRes));
  } catch (e) {
    console.error("[CompanyFullViewModal] Error fetching quotations/proposals:", e);
  }
};

const fetchCompanyProfileData = async (effectiveCompanyCode, q, setters) => {
  const { setDocuments, setNotes } = setters;
  if (!q) return;
  try {
    const res = await getCompanyProfile(effectiveCompanyCode, q);
    if (res?.success && res.profile) {
      if (Array.isArray(res.profile.documents)) {
        setDocuments(res.profile.documents);
      }
      if (Array.isArray(res.profile.notes) && res.profile.notes.length > 0) {
        setNotes(
          res.profile.notes.map((n, idx) => ({
            id: n._id || `note_${idx}_${Date.now()}`,
            date: n.createdAt ? dayjs(n.createdAt).format("YYYY-MM-DD hh:mm A") : "Recent",
            text: n.text,
          })),
        );
      }
    }
  } catch (e) {
    console.error("[CompanyFullViewModal] Error fetching company profile:", e);
  }
};

const resolveCompanyContext = (user, reduxCompanyCode, role) => {
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";
  const effectiveRole = role || user?.role || getCookie("role") || "company_admin";
  const isEmployee = effectiveRole === "employee" || effectiveRole === "sales_rep";
  return {
    effectiveCompanyCode,
    effectiveRole,
    isAdmin: !isEmployee,
  };
};

const resolveInitialRemarks = (lead, company) => {
  const aggregated = [];
  const addRem = (rem) => {
    if (!rem) return;
    const text = typeof rem === "string" ? rem.trim() : (rem?.text || rem?.remark || rem?.comment || rem?.note || "").trim();
    if (!text) return;
    const already = aggregated.some((existing) => {
      const exText = typeof existing === "string" ? existing.trim() : (existing?.text || existing?.remark || existing?.comment || existing?.note || "").trim();
      return exText === text;
    });
    if (!already) {
      aggregated.push(rem);
    }
  };

  if (Array.isArray(lead?.remarks)) {
    lead.remarks.forEach(addRem);
  }
  if (Array.isArray(company?.remarks)) {
    company.remarks.forEach(addRem);
  }
  const list = company?.leads || company?.contacts || [];
  list.forEach((c) => {
    if (Array.isArray(c?.remarks)) {
      c.remarks.forEach(addRem);
    }
  });
  return aggregated;
};

const filterProposalsList = (proposals, searchQuery) => {
  if (!searchQuery?.trim()) return proposals;
  const q = searchQuery.toLowerCase();
  return proposals.filter((p) => {
    return p.proposalNumber?.toLowerCase().includes(q) || p.leadCompanyName?.toLowerCase().includes(q) || p.templateName?.toLowerCase().includes(q) || p.contactName?.toLowerCase().includes(q) || p.leadCode?.toLowerCase().includes(q);
  });
};

const resolveLeadCode = (companyLead, company) => {
  if (companyLead?.leadId) return companyLead.leadId;
  if (company?.id) {
    const cleaned = String(company.id).replace(/\D/g, "");
    return `LD-${cleaned || "101"}`;
  }
  return "LD-101";
};

const resolveCompanyName = (company, companyLead, lead) => {
  return company?.name || companyLead?.leadCompanyName || lead?.leadCompanyName || companyLead?.contactName || "Company Full Profile";
};

const renderCompanyWebsite = (website) => {
  if (!website) {
    return <strong style={{ fontSize: 14, color: "#94a3b8" }}>—</strong>;
  }
  const href = website.startsWith("http") ? website : `https://${website}`;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: "#2459d3", fontWeight: 600, textDecoration: "underline", wordBreak: "break-all" }}>
      {website}
    </a>
  );
};

const PROPOSAL_COLUMNS = [
  {
    title: "Lead ID",
    dataIndex: "leadCode",
    key: "leadCode",
    width: 110,
    render: (code) => (
      <span
        style={{
          fontWeight: 700,
          color: "#0369a1",
          background: "#e0f2fe",
          padding: "3px 8px",
          borderRadius: 6,
          border: "1px solid #bae6fd",
          cursor: "pointer",
          fontSize: "0.82rem",
        }}
      >
        {code || "—"}
      </span>
    ),
  },
  {
    title: "Date",
    dataIndex: "proposalDate",
    key: "proposalDate",
    width: 130,
    render: (d) => <Text style={{ fontSize: "0.85rem", color: "#475569" }}>{d}</Text>,
  },
  {
    title: "Proposal",
    dataIndex: "proposalNumber",
    key: "proposalNumber",
    width: 160,
    render: (num) => (
      <Text strong style={{ color: "#0f172a", fontSize: "0.88rem" }}>
        {num}
      </Text>
    ),
  },
  {
    title: "Version",
    dataIndex: "versionNo",
    key: "versionNo",
    width: 90,
    render: (v) => (
      <span
        style={{
          display: "inline-block",
          padding: "2px 7px",
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 700,
          background: "#f3e8ff",
          color: "#7e22ce",
          border: "1px solid #e9d5ff",
        }}
      >
        v{v || 1}
      </span>
    ),
  },
  {
    title: "Company",
    dataIndex: "leadCompanyName",
    key: "leadCompanyName",
    render: (c) => (
      <Text strong style={{ color: "#1e293b" }}>
        {c}
      </Text>
    ),
  },
  {
    title: "Template",
    dataIndex: "templateName",
    key: "templateName",
    render: (t) => <span style={{ fontSize: "12px", color: "#64748b" }}>{t || "Default Template"}</span>,
  },
  {
    title: "Contact",
    key: "contact",
    render: (_, r) => <Text style={{ fontSize: "0.85rem", color: "#334155" }}>{r.contactName ? `${r.contactName} · ${r.contactNumber}` : r.contactNumber || "—"}</Text>,
  },
];

const INVOICE_COLUMNS = [
  {
    title: "Invoice #",
    dataIndex: "invoiceNumber",
    key: "invoiceNumber",
    render: (num, r) => (
      <a href={r.publicUrl || "#"} target={r.publicUrl ? "_blank" : "_self"} rel="noreferrer" style={{ color: "#2459d3", fontWeight: 600 }}>
        {num || "INV-NEW"}
      </a>
    ),
  },
  {
    title: "Date",
    dataIndex: "invoiceDate",
    key: "invoiceDate",
    render: (d, r) => {
      const dateVal = d || r.createdAt;
      return (
        <Text style={{ fontSize: "0.85rem", color: "#64748b" }}>
          {dateVal
            ? new Date(dateVal).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "—"}
        </Text>
      );
    },
  },
  {
    title: "Contact",
    dataIndex: "contactName",
    key: "contactName",
    render: (name, r) => <Text strong>{name || r.clientSnapshot?.contactName || "—"}</Text>,
  },
  {
    title: "Total Amount",
    dataIndex: "total",
    key: "total",
    render: (v) => <Text strong>₹ {Number(v || 0).toLocaleString("en-IN")}</Text>,
  },
  {
    title: "Paid",
    dataIndex: "amountPaid",
    key: "amountPaid",
    render: (v) => <Text style={{ color: "#10b981", fontWeight: 600 }}>₹ {Number(v || 0).toLocaleString("en-IN")}</Text>,
  },
  {
    title: "Status",
    dataIndex: "paymentStatus",
    key: "paymentStatus",
    render: (st) => {
      const s = String(st || "unpaid").toLowerCase();
      const color = s === "paid" ? "success" : s === "partially paid" || s === "partial" ? "gold" : "warning";
      return (
        <Tag color={color} style={{ borderRadius: 6, textTransform: "capitalize" }}>
          {s}
        </Tag>
      );
    },
  },
];

const getNavItems = (isAdmin) => {
  const iconSize = 17;
  const iconStroke = 1.6;
  const base = [
    { key: "overview", label: "Company Details", icon: <Building02Icon size={iconSize} strokeWidth={iconStroke} /> },
    { key: "remarks", label: "Overall History", icon: <Time02Icon size={iconSize} strokeWidth={iconStroke} /> },
    { key: "pipeline", label: "Pipeline", icon: <GitForkIcon size={iconSize} strokeWidth={iconStroke} /> },
    { key: "followups", label: "Schedule / Follow-up", icon: <Calendar01Icon size={iconSize} strokeWidth={iconStroke} /> },
    { key: "documents", label: "Documents", icon: <Folder01Icon size={iconSize} strokeWidth={iconStroke} /> },
  ];
  if (isAdmin) {
    base.push(
      { key: "invoices", label: "Invoice History", icon: <Invoice01Icon size={iconSize} strokeWidth={iconStroke} /> },
      { key: "quotations", label: "Quotation History", icon: <Calculator01Icon size={iconSize} strokeWidth={iconStroke} /> }
    );
  }
  base.push({ key: "notes", label: "Notes", icon: <NoteEditIcon size={iconSize} strokeWidth={iconStroke} /> });
  return base;
};

// Subcomponent: Company Overview Section
const CompanyDetailsSection = ({ companyName, leadCode, companyLead, company, isAdmin, openOverviewEditModal, contactsList, openContactAddModal, handleToggleSpoc, openDirectorEditModal, handleDeleteDirector }) => {
  return (
    <section
      id="fullview-section-overview"
      style={{
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid #d8dee8",
        padding: 16,
        marginBottom: 16,
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, borderBottom: "1px solid #f1f5f9", paddingBottom: 10 }}>
        <Title level={4} style={{ margin: 0, color: "#111827", fontSize: "1.15rem", fontWeight: 700 }}>
          Company Details
        </Title>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button size="small" icon={<EditOutlined />} onClick={openOverviewEditModal} style={{ borderRadius: 6, fontWeight: 600 }}>
            Edit Details
          </Button>
        </div>
      </div>

      {/* Overview Fields Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "12px 20px",
        }}
      >
        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Company Name</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyName}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Lead ID</span>
          <strong style={{ fontSize: 14, color: "#0369a1", fontWeight: 700 }}>{leadCode}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>CIN</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.cin || "—"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>GSTIN / GST Number</span>
          <strong style={{ fontSize: 14, color: companyLead?.gstNumber || companyLead?.gstin ? "#111827" : "#94a3b8" }}>{companyLead?.gstNumber || companyLead?.gstin || "—"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Incorporation Date</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{formatDisplayDate(companyLead?.dateOfIncorporation)}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Company Email</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.companyEmail || companyLead?.directorEmailAddress || "—"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Company Website</span>
          {renderCompanyWebsite(companyLead?.website || company?.website)}
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>ROC</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.roc || "—"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Registration No.</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.registrationNumber || "—"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Company Origin</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.companyOrigin || "—"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Company Type</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.companyType || "Private Limited"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Class of Company</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.classOfCompany || "—"}</strong>
        </div>

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Company Category</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.companyCategory || company?.category || "—"}</strong>
        </div>

        {isAdmin && (
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Subcategory</span>
            <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.companySubcategory || "—"}</strong>
          </div>
        )}

        <div>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 4 }}>Main Division</span>
          <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.mainDivisionDescription || companyLead?.mainDivisionNo || company?.category || "—"}</strong>
        </div>

        {/* Financials Row */}
        <div style={{ gridColumn: "span 3", borderTop: "1px solid #f1f5f9", marginTop: 6, paddingTop: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 10 }}>Financial Details</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px 20px" }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Authorised Capital</span>
              <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.authorisedCapital || "—"}</strong>
            </div>
            <div>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Paid-Up Capital</span>
              <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.paidUpCapital || "—"}</strong>
            </div>
            {isAdmin && (
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Total Obligation</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.totalObligationOfContribution || "—"}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Admin-only Address Details */}
        {isAdmin && (
          <div style={{ gridColumn: "span 3", borderTop: "1px solid #f1f5f9", marginTop: 6, paddingTop: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 10 }}>Address Details</span>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px 20px" }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Address Type</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.addressType || "—"}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Division No</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.mainDivisionNo || "—"}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Street Address 1</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.streetAddressLine1 || "—"}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Street Address 2</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.streetAddressLine2 || "—"}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>City</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.city || company?.city || "—"}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>State</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.state || "—"}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Postal Code</span>
                <strong style={{ fontSize: 14, color: "#111827" }}>{companyLead?.postalCode || "—"}</strong>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Directors / Contacts Section */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#2459d3", textTransform: "uppercase", letterSpacing: "0.05em" }}>Directors / Contacts ({contactsList.length})</span>
          <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={openContactAddModal} style={{ borderColor: "#2563eb", color: "#2563eb", fontWeight: 600, borderRadius: 6 }}>
            Add Director / Contact
          </Button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {contactsList.map((contact, idx) => {
            const permAddr = [contact.directorPermanentAddressLine1, contact.directorPermanentAddressLine2, contact.directorPermanentCity, contact.directorPermanentState, contact.directorPermanentPincode].filter(Boolean).join(", ");
            const presAddr = [contact.directorPresentAddressLine1, contact.directorPresentAddressLine2, contact.directorPresentCity, contact.directorPresentState, contact.directorPresentPincode].filter(Boolean).join(", ");

            return (
              <div
                key={contact._id || idx}
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <Flex justify="space-between" align="center" style={{ marginBottom: 12, borderBottom: "1px solid #e2e8f0", paddingBottom: 8 }}>
                  <Flex align="center" gap={8}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", textTransform: "uppercase" }}>Director {idx + 1}</span>
                    <Tag
                      style={{
                        background: "transparent",
                        backgroundColor: "transparent",
                        borderColor: "#0891b2",
                        color: "#0891b2",
                        borderRadius: 4,
                        fontWeight: 600,
                      }}
                    >
                      {contact.designation || "Director"}
                    </Tag>
                    {contact.isStarred && (
                      <Tag color="gold" style={{ borderRadius: 4, fontWeight: 600 }}>
                        SPOC
                      </Tag>
                    )}
                  </Flex>
                  <Flex align="center" gap={8}>
                    <Button
                      size="small"
                      icon={contact.isStarred ? <StarFilled style={{ color: "#eab308" }} /> : <StarOutlined />}
                      onClick={() => handleToggleSpoc(contact)}
                      style={{
                        borderRadius: 4,
                        color: contact.isStarred ? "#ca8a04" : "#64748b",
                        borderColor: contact.isStarred ? "#fef08a" : "#e2e8f0",
                        background: contact.isStarred ? "#fef9c3" : "#fff",
                        fontWeight: 600,
                      }}
                    >
                      {contact.isStarred ? "SPOC" : "Mark SPOC"}
                    </Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openDirectorEditModal(contact)} style={{ borderRadius: 4 }}>
                      Edit
                    </Button>
                    {contactsList.length > 1 && (
                      <Popconfirm title="Delete Director" description="Are you sure you want to delete this director / contact?" onConfirm={() => handleDeleteDirector(contact._id)} okText="Yes, Delete" cancelText="No" okButtonProps={{ danger: true }}>
                        <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 4 }}>
                          Delete
                        </Button>
                      </Popconfirm>
                    )}
                    <Tag color="blue">{contact.status || "New"}</Tag>
                  </Flex>
                </Flex>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "14px 20px" }}>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Contact Name</span>
                    <strong style={{ fontSize: 14, color: "#111827" }}>{contact.contactName || "—"}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Phone</span>
                    <strong style={{ fontSize: 14, color: "#111827" }}>{contact.contactNumber || contact.directorMobileNumber || "—"}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Mail</span>
                    <strong style={{ fontSize: 14, color: "#111827" }}>{contact.directorEmailAddress || contact.companyEmail || "—"}</strong>
                  </div>

                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>DIN</span>
                    <strong style={{ fontSize: 14, color: contact.directorDin ? "#111827" : "#94a3b8" }}>{contact.directorDin || "—"}</strong>
                  </div>

                  <div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Designation / Role</span>
                    <strong style={{ fontSize: 14, color: "#111827" }}>{contact.designation || "Director"}</strong>
                  </div>

                  {permAddr && (
                    <div style={{ gridColumn: "span 3" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Permanent Address</span>
                      <strong style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{permAddr}</strong>
                    </div>
                  )}

                  {presAddr && (
                    <div style={{ gridColumn: "span 3" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 2 }}>Present Address</span>
                      <strong style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{presAddr}</strong>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Subcomponent: Documents Repository Section
const CompanyDocumentsSection = ({ documents, filteredDocuments, docCategoryFilter, setDocCategoryFilter, companyName, setDocModalVisible, setPreviewDoc, setPreviewModalVisible, handleDeleteDocument }) => {
  const categories = ["All", "GST Certificate", "PAN Card", "Incorporation Certificate", "Agreement / Contract", "Bank Statement", "Identity Proof", "Other"];

  return (
    <section
      id="fullview-section-documents"
      style={{
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid #d8dee8",
        padding: 16,
        marginBottom: 16,
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#2459d3", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>Client Document Repository</div>
          <Title level={4} style={{ margin: 0, color: "#111827", fontSize: "1.15rem", fontWeight: 700 }}>
            Company Documents & Files ({documents.length})
          </Title>
          <Text type="secondary" style={{ fontSize: "0.82rem" }}>
            Upload and manage legal, tax, KYC certificates, and contracts for {companyName}.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setDocModalVisible(true)}
          style={{
            background: "#2459d3",
            borderRadius: 8,
            fontWeight: 600,
            boxShadow: "0 2px 4px rgba(36, 89, 211, 0.2)",
          }}
        >
          Upload Document
        </Button>
      </div>

      {/* Category Filter Chips */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 20,
          paddingBottom: 12,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        {categories.map((cat) => {
          const count = cat === "All" ? documents.length : documents.filter((d) => d.documentType === cat).length;
          const isSelected = docCategoryFilter === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setDocCategoryFilter(cat)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 12px",
                borderRadius: 20,
                border: isSelected ? "1px solid #2459d3" : "1px solid #e2e8f0",
                background: isSelected ? "#eff6ff" : "#ffffff",
                color: isSelected ? "#2459d3" : "#64748b",
                fontSize: "0.8rem",
                fontWeight: isSelected ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <span>{cat}</span>
              <span
                style={{
                  fontSize: "0.72rem",
                  padding: "1px 6px",
                  borderRadius: 10,
                  background: isSelected ? "#2459d3" : "#f1f5f9",
                  color: isSelected ? "#ffffff" : "#64748b",
                  fontWeight: 700,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <div
          style={{
            padding: "36px 20px",
            textAlign: "center",
            background: "#f8fafc",
            borderRadius: 12,
            border: "1px dashed #cbd5e1",
          }}
        >
          <FolderOpenOutlined style={{ fontSize: 36, color: "#94a3b8", marginBottom: 10 }} />
          <div style={{ fontWeight: 600, color: "#334155", fontSize: "0.95rem" }}>{docCategoryFilter === "All" ? "No client documents uploaded yet" : `No documents found under "${docCategoryFilter}"`}</div>
          <div style={{ fontSize: "0.82rem", color: "#64748b", maxWidth: 420, margin: "6px auto 16px" }}>Upload GST certificates, PAN cards, incorporation filings, or agreements for quick access and customer verification.</div>
          <Button type="dashed" icon={<UploadOutlined />} onClick={() => setDocModalVisible(true)} style={{ borderRadius: 8, borderColor: "#2459d3", color: "#2459d3" }}>
            Upload First Document
          </Button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {filteredDocuments.map((doc) => {
            const docId = doc._id || doc.id;
            return (
              <div
                key={docId}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  padding: "14px 16px",
                  background: "#ffffff",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                }}
              >
                <div>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>
                    {renderDocIcon(doc.documentType, doc.fileType)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={doc.name}
                      >
                        {doc.name}
                      </div>
                      <Tag color="blue" style={{ borderRadius: 6, fontSize: "0.72rem", marginTop: 4, fontWeight: 600 }}>
                        {doc.documentType || "General"}
                      </Tag>
                    </div>
                  </div>

                  <div style={{ fontSize: "0.75rem", color: "#64748b", display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <span>Size: {doc.fileSize || "—"}</span>
                    <span>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Recent"}</span>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 6,
                    paddingTop: 10,
                    borderTop: "1px solid #f1f5f9",
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined style={{ color: "#2459d3" }} />}
                    onClick={() => {
                      setPreviewDoc(doc);
                      setPreviewModalVisible(true);
                    }}
                  >
                    View
                  </Button>
                  <a href={doc.fileUrl} download={doc.fileName || doc.name || "document"} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex" }}>
                    <Button type="text" size="small" icon={<DownloadOutlined style={{ color: "#10b981" }} />}>
                      Download
                    </Button>
                  </a>
                  <Popconfirm title="Delete Document" description="Are you sure you want to delete this document?" okText="Yes, Delete" cancelText="Cancel" okButtonProps={{ danger: true }} onConfirm={() => handleDeleteDocument(docId)}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// Subcomponent: Edit Overview Modal
const EditOverviewModal = ({ open, onCancel, onOk, confirmLoading, companyName, draft, setDraft }) => (
  <Modal title={<div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Edit Company Details — {companyName}</div>} open={open} onCancel={onCancel} onOk={onOk} confirmLoading={confirmLoading} okText="Save Details" width={720} destroyOnClose>
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 12 }}>
      <Row gutter={16}>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Company Email</span>
          <Input placeholder="e.g. info@company.com" value={draft.companyEmail} onChange={(e) => setDraft({ ...draft, companyEmail: e.target.value })} />
        </Col>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Company Website</span>
          <Input placeholder="e.g. https://company.com" value={draft.website} onChange={(e) => setDraft({ ...draft, website: e.target.value })} />
        </Col>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>GSTIN / GST Number</span>
          <Input placeholder="e.g. 37ABCDE1234F1Z5" value={draft.gstNumber} onChange={(e) => setDraft({ ...draft, gstNumber: e.target.value, gstin: e.target.value })} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Date of Incorporation</span>
          <Input placeholder="e.g. 01 Jan 2025" value={draft.dateOfIncorporation} onChange={(e) => setDraft({ ...draft, dateOfIncorporation: e.target.value })} />
        </Col>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Company Type</span>
          <Input placeholder="Private Limited" value={draft.companyType} onChange={(e) => setDraft({ ...draft, companyType: e.target.value })} />
        </Col>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Class of Company</span>
          <Input placeholder="e.g. Private" value={draft.classOfCompany} onChange={(e) => setDraft({ ...draft, classOfCompany: e.target.value })} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>CIN</span>
          <Input placeholder="CIN Number" value={draft.cin} onChange={(e) => setDraft({ ...draft, cin: e.target.value })} />
        </Col>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Company Category</span>
          <Input placeholder="Company Category" value={draft.companyCategory} onChange={(e) => setDraft({ ...draft, companyCategory: e.target.value })} />
        </Col>
        <Col span={8}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Subcategory</span>
          <Input placeholder="Company Subcategory" value={draft.companySubcategory} onChange={(e) => setDraft({ ...draft, companySubcategory: e.target.value })} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>ROC</span>
          <Input placeholder="ROC Office" value={draft.roc} onChange={(e) => setDraft({ ...draft, roc: e.target.value })} />
        </Col>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Registration Number</span>
          <Input placeholder="Registration No" value={draft.registrationNumber} onChange={(e) => setDraft({ ...draft, registrationNumber: e.target.value })} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Authorised Capital</span>
          <Input placeholder="e.g. ₹ 10,00,000" value={draft.authorisedCapital} onChange={(e) => setDraft({ ...draft, authorisedCapital: formatAmountWithCommas(e.target.value) })} />
        </Col>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Paid-Up Capital</span>
          <Input placeholder="e.g. ₹ 1,00,000" value={draft.paidUpCapital} onChange={(e) => setDraft({ ...draft, paidUpCapital: formatAmountWithCommas(e.target.value) })} />
        </Col>
      </Row>

      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Main Division Description</span>
        <Input.TextArea rows={2} placeholder="Main division activities..." value={draft.mainDivisionDescription} onChange={(e) => setDraft({ ...draft, mainDivisionDescription: e.target.value })} />
      </div>

      <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Address Details</span>
        <Row gutter={16} style={{ marginBottom: 10 }}>
          <Col span={12}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Street Address 1</span>
            <Input placeholder="Address Line 1" value={draft.streetAddressLine1} onChange={(e) => setDraft({ ...draft, streetAddressLine1: e.target.value })} />
          </Col>
          <Col span={12}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Street Address 2</span>
            <Input placeholder="Address Line 2" value={draft.streetAddressLine2} onChange={(e) => setDraft({ ...draft, streetAddressLine2: e.target.value })} />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>City</span>
            <Input placeholder="City" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
          </Col>
          <Col span={8}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>State</span>
            <Input placeholder="State" value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value })} />
          </Col>
          <Col span={8}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Postal Code</span>
            <Input placeholder="Pincode" value={draft.postalCode} onChange={(e) => setDraft({ ...draft, postalCode: e.target.value })} />
          </Col>
        </Row>
      </div>
    </div>
  </Modal>
);

// Subcomponent: Edit Director Modal
const EditDirectorModal = ({ open, onCancel, onOk, confirmLoading, draft, setDraft }) => (
  <Modal title={<div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Edit {draft.designation || "Director"} Details</div>} open={open} onCancel={onCancel} onOk={onOk} confirmLoading={confirmLoading} okText={`Save ${draft.designation || "Director"}`} width={560} destroyOnClose>
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 12 }}>
      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Contact Name / Full Name</span>
        <Input placeholder="e.g. Rahul Sharma" value={draft.contactName} onChange={(e) => setDraft({ ...draft, contactName: e.target.value })} />
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Phone / Mobile Number</span>
          <Input
            placeholder="Phone number"
            value={draft.contactNumber}
            onChange={(e) =>
              setDraft({
                ...draft,
                contactNumber: e.target.value,
                directorMobileNumber: e.target.value,
              })
            }
          />
        </Col>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>DIN (Director Identification No)</span>
          <Input placeholder="e.g. 08765432" value={draft.directorDin} onChange={(e) => setDraft({ ...draft, directorDin: e.target.value })} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Email Address</span>
          <Input placeholder="e.g. director@company.com" value={draft.directorEmailAddress} onChange={(e) => setDraft({ ...draft, directorEmailAddress: e.target.value })} />
        </Col>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Designation / Role</span>
          <Select
            value={draft.designation || "Director"}
            onChange={(val) => setDraft({ ...draft, designation: val })}
            style={{ width: "100%" }}
            options={[
              { label: "Director", value: "Director" },
              { label: "Manager", value: "Manager" },
              { label: "Relative", value: "Relative" },
              { label: "Friends", value: "Friends" },
            ]}
          />
        </Col>
      </Row>
    </div>
  </Modal>
);

// Subcomponent: Add Contact Modal
const AddContactModal = ({ open, onCancel, onOk, confirmLoading, companyName, draft, setDraft }) => (
  <Modal title={<div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Add New {draft.designation || "Director"} to {companyName}</div>} open={open} onCancel={onCancel} onOk={onOk} confirmLoading={confirmLoading} okText={`Add ${draft.designation || "Director"}`} width={560} destroyOnClose>
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 12 }}>
      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Contact Name / Full Name *</span>
        <Input placeholder="e.g. Anjali Verma" value={draft.contactName} onChange={(e) => setDraft({ ...draft, contactName: e.target.value })} />
      </div>

      <Row gutter={16}>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Phone / Mobile Number</span>
          <Input
            placeholder="Phone number"
            value={draft.contactNumber}
            onChange={(e) =>
              setDraft({
                ...draft,
                contactNumber: e.target.value,
                directorMobileNumber: e.target.value,
              })
            }
          />
        </Col>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>DIN (Optional)</span>
          <Input placeholder="e.g. 09123456" value={draft.directorDin} onChange={(e) => setDraft({ ...draft, directorDin: e.target.value })} />
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Email Address</span>
          <Input placeholder="e.g. contact@company.com" value={draft.directorEmailAddress} onChange={(e) => setDraft({ ...draft, directorEmailAddress: e.target.value })} />
        </Col>
        <Col span={12}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Designation / Role</span>
          <Select
            value={draft.designation || "Director"}
            onChange={(val) => setDraft({ ...draft, designation: val })}
            style={{ width: "100%" }}
            options={[
              { label: "Director", value: "Director" },
              { label: "Manager", value: "Manager" },
              { label: "Relative", value: "Relative" },
              { label: "Friends", value: "Friends" },
            ]}
          />
        </Col>
      </Row>

      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Lead Status</span>
        <Select
          value={draft.status}
          onChange={(val) => setDraft({ ...draft, status: val })}
          style={{ width: "100%" }}
          options={[
            { label: "New", value: "New" },
            { label: "Contacted", value: "Contacted" },
            { label: "Interested", value: "Interested" },
            { label: "Deal Closed", value: "Deal Closed" },
          ]}
        />
      </div>
    </div>
  </Modal>
);

// Subcomponent: Upload Document Modal
const UploadDocumentModal = ({ open, onCancel, onOk, confirmLoading, companyName, draft, setDraft, handleFileSelect }) => (
  <Modal title={<div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Upload Client Document — {companyName}</div>} open={open} onCancel={onCancel} onOk={onOk} confirmLoading={confirmLoading} okText="Upload & Save" width={560} destroyOnClose>
    <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 12 }}>
      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Document Title / Name *</span>
        <Input placeholder="e.g. GST Registration Certificate, Company PAN Card" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      </div>

      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Document Category / Type</span>
        <Select
          value={draft.documentType}
          onChange={(val) => setDraft({ ...draft, documentType: val })}
          style={{ width: "100%" }}
          options={[
            { label: "GST Certificate", value: "GST Certificate" },
            { label: "PAN Card", value: "PAN Card" },
            { label: "Incorporation Certificate", value: "Incorporation Certificate" },
            { label: "Agreement / Contract", value: "Agreement / Contract" },
            { label: "Bank Statement / Cheque", value: "Bank Statement" },
            { label: "Identity Proof (Aadhaar/Passport)", value: "Identity Proof" },
            { label: "Invoice / Bill", value: "Invoice / Bill" },
            { label: "Other Attachment", value: "Other" },
          ]}
        />
      </div>

      <div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 }}>Select File to Upload *</span>
        <label
          htmlFor="client-doc-file-input"
          style={{
            border: "2px dashed #cbd5e1",
            borderRadius: 12,
            padding: "24px 16px",
            textAlign: "center",
            background: "#f8fafc",
            cursor: "pointer",
            position: "relative",
            display: "block",
          }}
        >
          <input id="client-doc-file-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.txt" onChange={handleFileSelect} style={{ display: "none" }} />
          {draft.fileUrl ? (
            <div>
              <div style={{ fontSize: 28, color: "#10b981", marginBottom: 6 }}>✓</div>
              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>{draft.fileName}</div>
              <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: 2 }}>Size: {draft.fileSize}</div>
              <div style={{ fontSize: "0.75rem", color: "#2459d3", marginTop: 8, fontWeight: 600 }}>Click to change file</div>
            </div>
          ) : (
            <div>
              <UploadOutlined style={{ fontSize: 32, color: "#2459d3", marginBottom: 8 }} />
              <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.9rem" }}>Click or drag file to this area to upload</div>
              <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4 }}>Supports PDF, PNG, JPG, DOCX, XLSX, TXT (up to 50MB)</div>
            </div>
          )}
        </label>
      </div>
    </div>
  </Modal>
);

const renderPreviewBody = (previewDoc) => {
  const isImage = previewDoc?.fileUrl?.startsWith("data:image/") || String(previewDoc?.fileType).includes("image");
  const isPdf = previewDoc?.fileUrl?.startsWith("data:application/pdf") || String(previewDoc?.fileType).includes("pdf");
  if (isImage) {
    return <img src={previewDoc.fileUrl} alt={previewDoc.name} style={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain", borderRadius: 6 }} />;
  }
  if (isPdf) {
    return <iframe src={previewDoc.fileUrl} title={previewDoc.name} style={{ width: "100%", height: "65vh", border: "none", borderRadius: 6 }} />;
  }
  return (
    <div style={{ textAlign: "center", padding: 40 }}>
      <FileTextOutlined style={{ fontSize: 48, color: "#2459d3", marginBottom: 12 }} />
      <div style={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>{previewDoc?.fileName || previewDoc?.name}</div>
      <div style={{ color: "#64748b", fontSize: "0.85rem", marginTop: 4 }}>This file type cannot be previewed directly inline.</div>
      <a href={previewDoc?.fileUrl} download={previewDoc?.fileName || "document"} target="_blank" rel="noopener noreferrer">
        <Button type="primary" icon={<DownloadOutlined />} style={{ marginTop: 16 }}>
          Download to View
        </Button>
      </a>
    </div>
  );
};

// Subcomponent: Preview Document Modal
const PreviewDocumentModal = ({ open, onCancel, previewDoc }) => (
  <Modal
    title={<div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Preview: {previewDoc?.name || "Document"}</div>}
    open={open}
    onCancel={onCancel}
    footer={[
      <a key="download" href={previewDoc?.fileUrl} download={previewDoc?.fileName || previewDoc?.name || "document"} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
        <Button icon={<DownloadOutlined />}>Download</Button>
      </a>,
      <Button key="close" type="primary" onClick={onCancel}>
        Close
      </Button>,
    ]}
    width={800}
    destroyOnClose
  >
    <div style={{ minHeight: 400, display: "flex", justifyContent: "center", alignItems: "center", background: "#f8fafc", borderRadius: 8, padding: 12, overflow: "hidden" }}>{renderPreviewBody(previewDoc)}</div>
  </Modal>
);

function CompanyFullViewModal({ visible, onClose, company, lead, role }) {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const { effectiveCompanyCode, isAdmin } = useMemo(() => resolveCompanyContext(user, reduxCompanyCode, role), [user, reduxCompanyCode, role]);

  const [activeSection, setActiveSection] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [deals, setDeals] = useState([]);

  // Documents State
  const [documents, setDocuments] = useState([]);
  const [docCategoryFilter, setDocCategoryFilter] = useState("All");
  const [docModalVisible, setDocModalVisible] = useState(false);
  const [docSaving, setDocSaving] = useState(false);
  const [docDraft, setDocDraft] = useState({
    name: "",
    documentType: "GST Certificate",
    fileUrl: "",
    fileName: "",
    fileSize: "",
    fileType: "",
  });
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  // Notes & Remarks State
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [remarksList, setRemarksList] = useState(() => resolveInitialRemarks(lead, company));
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [remarkDraft, setRemarkDraft] = useState("");

  const scrollContainerRef = useRef(null);

  const [activeCompanyLead, setActiveCompanyLead] = useState(() => lead || company?.leads?.[0] || company?.contacts?.[0] || {});
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    const list = company?.leads || company?.contacts || (lead ? [lead] : []);
    setContacts(list);
    setActiveCompanyLead(lead || company?.leads?.[0] || company?.contacts?.[0] || {});
    setRemarksList(resolveInitialRemarks(lead, company));
  }, [company, lead]);

  const companyLead = activeCompanyLead;
  const companyName = useMemo(() => resolveCompanyName(company, companyLead, lead), [company, companyLead, lead]);
  const leadCode = useMemo(() => resolveLeadCode(companyLead, company), [companyLead, company]);
  const contactsList = contacts;
  const isStarred = useMemo(() => {
    return Boolean(companyLead?.isFavourite || companyLead?.isStarred || lead?.isFavourite || lead?.isStarred || company?.isFavourite || company?.isStarred || contacts?.some((c) => c?.isFavourite || c?.isStarred));
  }, [companyLead, lead, company, contacts]);

  // Overview Edit State
  const [overviewModalVisible, setOverviewModalVisible] = useState(false);
  const [overviewSaving, setOverviewSaving] = useState(false);
  const [overviewDraft, setOverviewDraft] = useState({});

  // Director Edit Modal State
  const [directorModalVisible, setDirectorModalVisible] = useState(false);
  const [directorSaving, setDirectorSaving] = useState(false);
  const [directorDraft, setDirectorDraft] = useState({});

  // Add Contact Modal State
  const [contactAddModalVisible, setContactAddModalVisible] = useState(false);
  const [contactAddSaving, setContactAddSaving] = useState(false);
  const [contactAddDraft, setContactAddDraft] = useState({
    contactName: "",
    contactNumber: "",
    directorEmailAddress: "",
    directorDin: "",
    status: "New",
  });

  // Fetch Live Invoices, Quotations, Proposals & Deals for this company/lead
  useEffect(() => {
    if (visible && (company || lead)) {
      const q = company?.name || lead?.leadCompanyName || lead?.companyName || lead?.contactName || "";
      if (isAdmin) {
        fetchAdminCompanyData(effectiveCompanyCode, q, { setInvoices, setProposals });
      }
      getDeals(effectiveCompanyCode, { search: q }).then((res) => {
        if (res?.success && Array.isArray(res.deals)) {
          setDeals(res.deals);
        } else {
          setDeals([]);
        }
      });
      fetchCompanyProfileData(effectiveCompanyCode, q, { setDocuments, setNotes });
      if (q) {
        setHistoryLoading(true);
        getCompanyHistory(effectiveCompanyCode, { companyName: q })
          .then((res) => {
            if (res?.success && Array.isArray(res.logs)) {
              setHistoryLogs(res.logs);
            } else {
              setHistoryLogs([]);
            }
          })
          .catch((err) => {
            console.error("Failed to load company history:", err);
            setHistoryLogs([]);
          })
          .finally(() => {
            setHistoryLoading(false);
          });
      }
    }
  }, [visible, company, lead, effectiveCompanyCode, isAdmin]);

  // Handle Document File Selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeStr = formatFileSize(file.size);
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      setDocDraft((prev) => ({
        ...prev,
        name: prev.name || file.name.replace(/\.[^/.]+$/, ""),
        fileName: file.name,
        fileSize: sizeStr,
        fileType: file.type,
        fileUrl: dataUrl,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveDocument = async () => {
    if (!docDraft.name?.trim()) {
      message.warning("Please enter a document title.");
      return;
    }
    if (!docDraft.fileUrl) {
      message.warning("Please select or upload a document file.");
      return;
    }

    setDocSaving(true);
    try {
      const payload = {
        name: docDraft.name.trim(),
        documentType: docDraft.documentType || "General",
        fileUrl: docDraft.fileUrl,
        fileName: docDraft.fileName || docDraft.name,
        fileSize: docDraft.fileSize || "—",
        fileType: docDraft.fileType || "",
        uploadedBy: user?.name || "Team Member",
      };

      const res = await uploadCompanyDocument(effectiveCompanyCode, companyName, payload);
      if (res?.success !== false) {
        if (res?.documents) {
          setDocuments(res.documents);
        } else if (res?.profile?.documents) {
          setDocuments(res.profile.documents);
        } else {
          setDocuments((prev) => [payload, ...prev]);
        }
        message.success("Document uploaded successfully!");
        setDocModalVisible(false);
        setDocDraft({
          name: "",
          documentType: "GST Certificate",
          fileUrl: "",
          fileName: "",
          fileSize: "",
          fileType: "",
        });
      } else {
        message.error(res?.message || "Failed to upload document.");
      }
    } catch (err) {
      console.error("Document upload error:", err);
      message.error("An error occurred while uploading document.");
    } finally {
      setDocSaving(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      const res = await deleteCompanyDocument(effectiveCompanyCode, companyName, docId);
      if (res?.success !== false) {
        setDocuments((prev) => prev.filter((d) => (d._id || d.id) !== docId));
        message.success("Document removed successfully.");
      } else {
        message.error(res?.message || "Failed to delete document.");
      }
    } catch (err) {
      console.error("Document delete error:", err);
      message.error("An error occurred while deleting document.");
    }
  };

  const filteredDocuments = useMemo(() => {
    if (docCategoryFilter === "All") return documents;
    return documents.filter((d) => d.documentType === docCategoryFilter);
  }, [documents, docCategoryFilter]);

  const filteredProposals = useMemo(() => {
    return filterProposalsList(proposals, searchQuery);
  }, [proposals, searchQuery]);

  const openOverviewEditModal = () => {
    setOverviewDraft({
      companyEmail: companyLead.companyEmail || "",
      website: companyLead.website || company?.website || "",
      companyType: companyLead.companyType || "Private Limited",
      classOfCompany: companyLead.classOfCompany || "",
      companyCategory: companyLead.companyCategory || "",
      companySubcategory: companyLead.companySubcategory || "",
      dateOfIncorporation: formatDisplayDate(companyLead.dateOfIncorporation) === "—" ? "" : formatDisplayDate(companyLead.dateOfIncorporation),
      roc: companyLead.roc || "",
      registrationNumber: companyLead.registrationNumber || "",
      mainDivisionDescription: companyLead.mainDivisionDescription || companyLead.category || "",
      mainDivisionNo: companyLead.mainDivisionNo || "",
      authorisedCapital: companyLead.authorisedCapital || "",
      paidUpCapital: companyLead.paidUpCapital || "",
      totalObligationOfContribution: companyLead.totalObligationOfContribution || "",
      addressType: companyLead.addressType || "",
      streetAddressLine1: companyLead.streetAddressLine1 || "",
      streetAddressLine2: companyLead.streetAddressLine2 || "",
      city: companyLead.city || "",
      state: companyLead.state || "",
      postalCode: companyLead.postalCode || "",
      cin: companyLead.cin || "",
      gstNumber: companyLead.gstNumber || companyLead.gstin || "",
      gstin: companyLead.gstNumber || companyLead.gstin || "",
    });
    setOverviewModalVisible(true);
  };

  const handleSaveOverview = async () => {
    if (!companyLead?._id) {
      message.error("Cannot find lead ID to update.");
      return;
    }
    setOverviewSaving(true);
    try {
      const payload = {
        ...overviewDraft,
        gstNumber: overviewDraft.gstNumber || overviewDraft.gstin || "",
        gstin: overviewDraft.gstNumber || overviewDraft.gstin || "",
      };
      const res = await updateLeadOverview(effectiveCompanyCode, companyLead._id, payload);
      if (res?.success !== false) {
        const savedLead = res?.lead || payload;
        setActiveCompanyLead((prev) => ({ ...prev, ...savedLead }));
        message.success("Company details updated successfully!");
        setOverviewModalVisible(false);
      } else {
        message.error(res?.message || "Failed to update company details.");
      }
    } catch (err) {
      console.error("Overview update error:", err);
      message.error("An error occurred while updating company details.");
    } finally {
      setOverviewSaving(false);
    }
  };

  const openDirectorEditModal = (contact) => {
    setDirectorDraft({
      _id: contact._id,
      contactName: contact.contactName || "",
      directorFirstName: contact.directorFirstName || "",
      directorLastName: contact.directorLastName || "",
      contactNumber: contact.contactNumber || contact.directorMobileNumber || "",
      directorMobileNumber: contact.directorMobileNumber || contact.contactNumber || "",
      directorEmailAddress: contact.directorEmailAddress || contact.companyEmail || "",
      directorDin: contact.directorDin || "",
      designation: contact.designation || "Director",
    });
    setDirectorModalVisible(true);
  };

  const handleSaveDirector = async () => {
    if (!directorDraft?._id) {
      message.error("Director ID not found.");
      return;
    }
    setDirectorSaving(true);
    try {
      const nameParts = (directorDraft.contactName || "").trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      const updatedPayload = {
        ...directorDraft,
        directorFirstName: firstName,
        directorLastName: lastName,
      };

      const res = await updateLeadDirector(effectiveCompanyCode, directorDraft._id, updatedPayload);
      if (res?.success !== false) {
        setContacts((prev) => prev.map((c) => (c._id === directorDraft._id ? { ...c, ...updatedPayload } : c)));
        if (companyLead._id === directorDraft._id) {
          setActiveCompanyLead((prev) => ({ ...prev, ...updatedPayload }));
        }
        message.success("Director details updated successfully!");
        setDirectorModalVisible(false);
      } else {
        message.error(res?.message || "Failed to update director.");
      }
    } catch (err) {
      console.error("Director update error:", err);
      message.error("An error occurred while updating director.");
    } finally {
      setDirectorSaving(false);
    }
  };

  const openContactAddModal = () => {
    setContactAddDraft({
      contactName: "",
      contactNumber: "",
      directorEmailAddress: "",
      directorDin: "",
      designation: "Director",
      status: "New",
    });
    setContactAddModalVisible(true);
  };

  const handleSaveContactAdd = async () => {
    if (!contactAddDraft.contactName?.trim()) {
      message.warning("Please provide a contact name.");
      return;
    }
    setContactAddSaving(true);
    try {
      const nameParts = (contactAddDraft.contactName || "").trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      const payload = {
        ...contactAddDraft,
        contactName: contactAddDraft.contactName.trim(),
        directorFirstName: firstName,
        directorLastName: lastName,
        leadCompanyName: companyName,
        companyName: companyName,
        assignedEmployeeId: companyLead.assignedEmployeeId,
        assignedEmployeeName: companyLead.assignedEmployeeName,
        assignedEmployeePhone: companyLead.assignedEmployeePhone,
        city: companyLead.city,
        state: companyLead.state,
      };
      const res = await createLead(effectiveCompanyCode, payload);
      if (res?.success !== false) {
        const created = res.lead || payload;
        setContacts((prev) => [...prev, created]);
        message.success("New director / contact added successfully!");
        setContactAddModalVisible(false);
      } else {
        message.error(res?.message || "Failed to add director.");
      }
    } catch (err) {
      console.error("Add director error:", err);
      message.error("An error occurred while adding director.");
    } finally {
      setContactAddSaving(false);
    }
  };

  const handleDeleteDirector = async (contactId) => {
    if (!contactId) return;
    try {
      const res = await deleteLead(effectiveCompanyCode, contactId);
      if (res?.success !== false) {
        setContacts((prev) => prev.filter((c) => c._id !== contactId));
        message.success("Director / contact deleted successfully!");
      } else {
        message.error(res?.message || "Failed to delete director.");
      }
    } catch (err) {
      console.error("Delete director error:", err);
      message.error("An error occurred while deleting director.");
    }
  };

  const handleToggleSpoc = async (contact) => {
    if (!contact?._id) return;
    try {
      const newStarred = !contact.isStarred;
      const res = await updateLeadFlags(effectiveCompanyCode, contact._id, { isStarred: newStarred });
      if (res?.success !== false) {
        setContacts((prev) => prev.map((c) => (c._id === contact._id ? { ...c, isStarred: newStarred } : c)));
        message.success(newStarred ? "Marked as SPOC" : "Unmarked SPOC");
      }
    } catch (err) {
      console.error("Toggle SPOC error:", err);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(`fullview-section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleAddNote = () => {
    if (!noteDraft.trim()) return;
    const newNote = {
      id: `n-${Date.now()}`,
      date: new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }),
      text: noteDraft.trim(),
    };
    setNotes((prev) => [newNote, ...prev]);
    setNoteDraft("");
    message.success("Note added!");
  };

  const handleAddRemark = async () => {
    if (!remarkDraft.trim()) return;
    const text = remarkDraft.trim();
    const newRemark = {
      text,
      date: new Date().toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }),
    };
    setRemarksList((prev) => [newRemark, ...prev]);
    setRemarkDraft("");

    const targetLeadId = companyLead?._id || companyLead?.id || lead?._id || lead?.id || contacts?.[0]?._id || contacts?.[0]?.id;
    if (targetLeadId) {
      try {
        await addLeadRemark(effectiveCompanyCode, targetLeadId, text);
        const q = company?.name || lead?.leadCompanyName || lead?.companyName || lead?.contactName || "";
        if (q) {
          getCompanyHistory(effectiveCompanyCode, { companyName: q }).then((res) => {
            if (res?.success && Array.isArray(res.logs)) {
              setHistoryLogs(res.logs);
            }
          });
        }
      } catch (err) {
        console.error("Failed to persist lead remark:", err);
      }
    }
    message.success("Remark added!");
  };

  const navItems = useMemo(() => getNavItems(isAdmin), [isAdmin]);

  if (!company && !lead) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="min(1360px, 94vw)"
      className="company-fullview-modal"
      wrapClassName="company-fullview-modal-wrap"
      rootClassName="company-fullview-modal-root"
      style={{ top: "3vh", paddingBottom: 0 }}
      styles={{
        content: { height: "94vh", maxHeight: "94vh", padding: 0, borderRadius: 16, overflow: "hidden" },
        body: { height: "94vh", maxHeight: "94vh", padding: 0, display: "flex", overflow: "hidden" },
      }}
    >
      {/* ── LEFT SIDEBAR NAVIGATION ── */}
      <div
        style={{
          width: 220,
          background: "#ffffff",
          borderRight: "1px solid #e2e8f0",
          padding: "20px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            minHeight: 40,
            marginBottom: 16,
            padding: "0 14px",
            border: "1px solid #d8dee8",
            borderRadius: 999,
            background: "#ffffff",
            color: "#111827",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          }}
        >
          <ArrowLeft02Icon size={15} strokeWidth={1.8} />
          <span>Close</span>
        </button>

        {navItems.map((item) => {
          const isActive = activeSection === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => scrollToSection(item.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: "10px 14px",
                border: "none",
                borderRadius: 8,
                background: isActive ? "#eff6ff" : "transparent",
                color: isActive ? "#2459d3" : "#64748b",
                fontWeight: isActive ? 700 : 500,
                fontSize: "0.9rem",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isActive ? "#2459d3" : "#64748b",
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── RIGHT MAIN CONTINUOUS SCROLL PANE ── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "#f3f5fb",
        }}
      >
        {/* Sticky Header Banner */}
        <div
          style={{
            padding: "12px 20px",
            paddingRight: 60,
            borderBottom: "1px solid #d8dee8",
            background: "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
            minHeight: 52,
          }}
        >
          {/* Left: Company Name */}
          <Title
            level={3}
            style={{
              margin: 0,
              color: "#111827",
              fontWeight: 700,
              fontSize: "1.15rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {companyName}
          </Title>

          {/* Middle: Lead Code & Starred Status */}
          {(leadCode || isStarred) && (
            <Flex
              align="center"
              justify="center"
              gap={8}
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#111827",
                flex: 1,
                padding: "0 16px",
                whiteSpace: "nowrap",
              }}
            >
              {leadCode && <span>{leadCode}</span>}
              {leadCode && isStarred && <span>|</span>}
              {isStarred && <span>★ Starred Lead</span>}
            </Flex>
          )}
        </div>

        {/* Continuous Scrollable Stream with All Sections */}
        <div
          ref={scrollContainerRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 20px 24px 20px",
            scrollBehavior: "smooth",
          }}
        >
          {/* ════ SECTION 1: COMPANY DETAILS ════ */}
          <CompanyDetailsSection
            companyName={companyName}
            leadCode={leadCode}
            companyLead={companyLead}
            company={company}
            isAdmin={isAdmin}
            openOverviewEditModal={openOverviewEditModal}
            contactsList={contactsList}
            openContactAddModal={openContactAddModal}
            handleToggleSpoc={handleToggleSpoc}
            openDirectorEditModal={openDirectorEditModal}
            handleDeleteDirector={handleDeleteDirector}
          />

          {/* ════ SECTION 2: OVERALL HISTORY ════ */}
          <section
            id="fullview-section-remarks"
            style={{
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #d8dee8",
              padding: 16,
              marginBottom: 16,
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            }}
          >
            <CompanyTimeline
              lead={companyLead}
              company={company}
              remarks={remarksList}
              invoices={invoices}
              proposals={proposals}
              historyLogs={historyLogs}
              loading={historyLoading}
            />

            {/* Quick remark entry box */}
            <div style={{ marginTop: 24, paddingTop: 18, borderTop: "1px solid #f1f5f9" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#475569", display: "block", marginBottom: 10 }}>Add New Remark</span>
              <Flex gap={10}>
                <Input placeholder="Type new follow-up remark or interaction note..." value={remarkDraft} onChange={(e) => setRemarkDraft(e.target.value)} onPressEnter={handleAddRemark} style={{ borderRadius: 8, height: 38 }} />
                <Button type="primary" onClick={handleAddRemark} disabled={!remarkDraft.trim()} style={{ borderRadius: 8, height: 38, padding: "0 20px" }}>
                  Add Remark
                </Button>
              </Flex>
            </div>
          </section>

          {/* ════ SECTION 3: PIPELINE ════ */}
          <section
            id="fullview-section-pipeline"
            style={{
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #d8dee8",
              padding: 16,
              marginBottom: 16,
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: "#111827", fontSize: "1.15rem", fontWeight: 700 }}>
                Pipeline Deals ({deals.length})
              </Title>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 }}>
              {deals.map((d) => (
                <div
                  key={d.id}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: 16,
                    background: "#f8fafc",
                  }}
                >
                  <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                    <Text strong style={{ fontSize: "0.95rem", color: "#0f172a" }}>
                      {d.dealName}
                    </Text>
                    <Tag color="blue">{d.pipelineStage}</Tag>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text type="secondary">
                      Amount: <strong style={{ color: "#111827" }}>₹ {d.amount.toLocaleString()}</strong>
                    </Text>
                    <Text type="secondary">Target: {formatDisplayDate(d.closingDate)}</Text>
                  </Flex>
                </div>
              ))}
            </div>
          </section>

          {/* ════ SECTION 4: SCHEDULE / FOLLOW-UP (Reusable) ════ */}
          <section
            id="fullview-section-followups"
            style={{
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #d8dee8",
              padding: 16,
              marginBottom: 16,
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: "#111827", fontSize: "1.15rem", fontWeight: 700 }}>
                Schedule / Follow-up
              </Title>
            </div>

            <ScheduleFollowupView companyName={companyName} contactName={lead?.contactName} embedded={true} />
          </section>

          {/* ════ SECTION 5: CLIENT DOCUMENTS & ATTACHMENTS ════ */}
          <CompanyDocumentsSection documents={documents} filteredDocuments={filteredDocuments} docCategoryFilter={docCategoryFilter} setDocCategoryFilter={setDocCategoryFilter} companyName={companyName} setDocModalVisible={setDocModalVisible} setPreviewDoc={setPreviewDoc} setPreviewModalVisible={setPreviewModalVisible} handleDeleteDocument={handleDeleteDocument} />

          {/* ════ SECTION 6: INVOICE HISTORY (ADMIN ONLY) ════ */}
          {isAdmin && (
            <section
              id="fullview-section-invoices"
              style={{
                background: "#ffffff",
                borderRadius: 14,
                border: "1px solid #d8dee8",
                padding: 16,
                marginBottom: 16,
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Title level={4} style={{ margin: 0, color: "#111827", fontSize: "1.15rem", fontWeight: 700 }}>
                  Invoice History ({invoices.length})
                </Title>
              </div>

              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                <Table columns={INVOICE_COLUMNS} dataSource={invoices} rowKey="_id" pagination={false} size="middle" />
              </div>
            </section>
          )}

          {/* ════ SECTION 7: QUOTATION / PROPOSAL HISTORY (ADMIN ONLY) ════ */}
          {isAdmin && (
            <section
              id="fullview-section-quotations"
              style={{
                background: "#ffffff",
                borderRadius: 14,
                border: "1px solid #d8dee8",
                padding: 16,
                marginBottom: 16,
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
                <div>
                  <Title level={4} style={{ margin: 0, color: "#111827", fontSize: "1.15rem", fontWeight: 700 }}>
                    Quotation & Proposal History
                  </Title>
                  <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                    {filteredProposals.length} saved proposal versions
                  </Text>
                </div>

                <Flex align="center" gap={12}>
                  <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search proposals..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: 220, borderRadius: 8 }} allowClear />
                  <DatePicker.RangePicker value={dateRange} onChange={setDateRange} placeholder={["From", "To"]} style={{ borderRadius: 8 }} />
                </Flex>
              </div>

              <div
                className="table-scroll invoice-history-table"
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  overflow: "hidden",
                  background: "#ffffff",
                }}
              >
                <Table
                  columns={PROPOSAL_COLUMNS}
                  dataSource={filteredProposals}
                  rowKey="_id"
                  pagination={false}
                  size="middle"
                  onRow={(record) => ({
                    onClick: () => setSelectedProposal(record),
                    style: { cursor: "pointer" },
                  })}
                />
              </div>

              {selectedProposal && (
                <div
                  style={{
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 10,
                    padding: 16,
                    marginTop: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Flex vertical gap={4}>
                    <Text strong style={{ color: "#1e3a8a" }}>
                      Selected Proposal: {selectedProposal.proposalNumber} (v{selectedProposal.versionNo})
                    </Text>
                    <Text type="secondary" style={{ fontSize: "0.82rem" }}>
                      Template: {selectedProposal.templateName} &bull; Total: {selectedProposal.amount}
                    </Text>
                  </Flex>
                  <Button type="primary" size="small">
                    Download PDF
                  </Button>
                </div>
              )}
            </section>
          )}

          {/* ════ SECTION 8: NOTES ════ */}
          <section
            id="fullview-section-notes"
            style={{
              background: "#ffffff",
              borderRadius: 14,
              border: "1px solid #d8dee8",
              padding: 16,
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <Title level={4} style={{ margin: 0, color: "#111827", fontSize: "1.15rem", fontWeight: 700 }}>
                Notes & Internal Records
              </Title>
            </div>

            {/* Note Composer */}
            <Flex vertical gap={12} style={{ marginBottom: 20 }}>
              <Input.TextArea rows={3} placeholder="Add a dated note or internal remark for this company..." value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} style={{ borderRadius: 8 }} />
              <Flex justify="flex-end">
                <Button type="primary" onClick={handleAddNote} disabled={!noteDraft.trim()} style={{ borderRadius: 8 }}>
                  Add Note
                </Button>
              </Flex>
            </Flex>

            <Flex vertical gap={12}>
              {notes.map((n) => (
                <div
                  key={n.id}
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    padding: 14,
                    borderRadius: 10,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: "0.78rem", display: "block", marginBottom: 4 }}>
                    {n.date}
                  </Text>
                  <Text style={{ fontSize: "0.92rem", color: "#1e293b" }}>{n.text}</Text>
                </div>
              ))}
            </Flex>
          </section>
        </div>
      </div>

      {/* ════ MODAL 1: EDIT COMPANY OVERVIEW DETAILS ════ */}
      <EditOverviewModal open={overviewModalVisible} onCancel={() => setOverviewModalVisible(false)} onOk={handleSaveOverview} confirmLoading={overviewSaving} companyName={companyName} draft={overviewDraft} setDraft={setOverviewDraft} />

      {/* ════ MODAL 2: EDIT DIRECTOR / CONTACT ════ */}
      <EditDirectorModal open={directorModalVisible} onCancel={() => setDirectorModalVisible(false)} onOk={handleSaveDirector} confirmLoading={directorSaving} draft={directorDraft} setDraft={setDirectorDraft} />

      {/* ════ MODAL 3: ADD NEW DIRECTOR / CONTACT ════ */}
      <AddContactModal open={contactAddModalVisible} onCancel={() => setContactAddModalVisible(false)} onOk={handleSaveContactAdd} confirmLoading={contactAddSaving} companyName={companyName} draft={contactAddDraft} setDraft={setContactAddDraft} />

      {/* ════ MODAL 4: UPLOAD CLIENT DOCUMENT ════ */}
      <UploadDocumentModal open={docModalVisible} onCancel={() => setDocModalVisible(false)} onOk={handleSaveDocument} confirmLoading={docSaving} companyName={companyName} draft={docDraft} setDraft={setDocDraft} handleFileSelect={handleFileSelect} />

      {/* ════ MODAL 5: DOCUMENT PREVIEW MODAL ════ */}
      <PreviewDocumentModal open={previewModalVisible} onCancel={() => setPreviewModalVisible(false)} previewDoc={previewDoc} />
    </Modal>
  );
}

export default CompanyFullViewModal;
